/**
 * draftCommit — the page-injected commit seam for converted sections whose
 * fields live in the page-level legacy draft (raised as blocker #3 by the §E
 * save-lane prep: sections must not import the page, but flat-field payload
 * composition lives page-level).
 *
 * BrandBiblePage provides `commitDraftFields(fields, letter)`: merge the
 * section's buffered flat-field edits into the page draft and persist them
 * NOW through the page's own save path, re-including `letter`'s columns past
 * the autosave-skip filter (lib/bbLegacyFieldOwnership). A converted
 * section's registered save() calls this for its flat fields; v2-homed
 * fields (bb_* rows) keep going through their own services and never need
 * this seam.
 *
 * `fields` is typed as Record<string, unknown> rather than
 * Partial<BrandBibleDraft> on purpose — BrandBibleDraft is declared in the
 * page module, and importing it here would recreate the section→page cycle
 * this directory exists to break. The page's provider narrows the type.
 */
import { createContext, useContext } from 'react';
import type { BbLetter } from '@/lib/bbSectionMap';

export type BbDraftCommit = (
  fields: Record<string, unknown>,
  letter: BbLetter,
) => Promise<boolean>;

const BbDraftCommitContext = createContext<BbDraftCommit | null>(null);

export const BbDraftCommitProvider = BbDraftCommitContext.Provider;

/** Null until the page mounts the provider — converted sections must treat a
 *  null seam as "flat-field commit unavailable" and fail their save() loudly
 *  rather than silently dropping the buffered fields. */
export function useBbDraftCommit(): BbDraftCommit | null {
  return useContext(BbDraftCommitContext);
}
